import React, { useState, useEffect } from 'react'
import { Button } from 'primereact/button'
import { TabView, TabPanel } from 'primereact/tabview'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Badge } from 'primereact/badge'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Card } from 'primereact/card'
import { Toast } from 'primereact/toast'
import { Chip } from 'primereact/chip'
import { Tooltip } from 'primereact/tooltip'
import { 
  FullFulfillmentOrder, 
  PartialFulfillmentOrder, 
  FailedOrder
} from '../../../services/bulkAssignmentService'
import { useBulkOrderAssignment } from '../../../hooks/useBulkOrderAssignment'
import { useOrderData } from '../../../hooks/useOrderData'
import { ViewOrderInfoDialog } from './../ViewOrderInfoDialog'
import { useLineItems } from '../../../hooks/useLineItems'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'

interface AssignmentExecutionResultsProps {
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  BulkOrderAssignment: ReturnType<typeof useBulkOrderAssignment>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
}


export const AssignmentExecutionResults: React.FC<AssignmentExecutionResultsProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  BulkOrderAssignment,  
  lineItemSelection
}) => {
  // Add state for ViewOrderInfoDialog
  const [isOrderDialogVisible, setIsOrderDialogVisible] = useState(false)
  
  // Add state for active tab management
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const {
    isProcessing,
    processedResults,
    toast,
    selectedFull,
    selectedPartial,
    totalSelected,
    handleOrderToggle,
    handleSubmitAssignment,
    isSubmitting,
    handleBackToSummary,
  } = BulkOrderAssignment

  // Auto-select first tab with data when results change
  useEffect(() => {
    if (processedResults) {
      let selectedIndex = 0
      
      // Priority 1: Full fulfillment orders
      if (processedResults.full && processedResults.full.length > 0) {
        selectedIndex = 0
      }
      // Priority 2: Partial fulfillment orders  
      else if (processedResults.partial && processedResults.partial.length > 0) {
        selectedIndex = 1
      }
      // Priority 3: Failed orders
      else if (processedResults.failed && processedResults.failed.length > 0) {
        selectedIndex = 2
      }
      
      setActiveTabIndex(selectedIndex)
    }
  }, [processedResults])

  // Add handler for viewing order details
  const handleViewOrderDetails = (order: FullFulfillmentOrder | PartialFulfillmentOrder | FailedOrder) => {
    // Find the full order object from the main orders array using order_id
    const fullOrder = orderData.orders.find(o => o.order_id === order.order_id)
    
    if (fullOrder) {
      orderSelection.selectOrder(fullOrder)
      setIsOrderDialogVisible(true)
    } else {
      // Show toast error if order not found
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Order details not found. Please refresh and try again.',
        life: 3000
      })
    }
  }

  // Don't render results content if still processing or no results yet
  if (isProcessing || !processedResults) {
    return (
      <div>
        {/* Processing Indicator */}
        <div className="text-center mb-4">
          <Card className="bg-blue-50">
            <div className="flex flex-column align-items-center gap-3">
              <ProgressSpinner style={{width: '50px', height: '50px'}} strokeWidth="4" />
              <div className="text-600 font-medium">Processing orders...</div>
              <div className="text-sm text-500">
                Analyzing orders for optimal assignment
              </div>
            </div>
          </Card>
        </div>
      </div>
    )
  }



  // Selection handlers for DataTable
  const handleFullSelectionChange = (e: any) => {
    const selectedIds = e.value.map((order: FullFulfillmentOrder) => order.order_id)
    // Update selection state for all selected/deselected orders
    const currentlySelected = new Set(selectedFull)
    const newSelection = new Set(selectedIds)
    
    // Find orders to add and remove
    const toAdd = selectedIds.filter((id: string) => !currentlySelected.has(id))
    const toRemove = selectedFull.filter(id => !newSelection.has(id))
    
    // Toggle each order
    toAdd.forEach((id: string) => handleOrderToggle(id, 'full'))
    toRemove.forEach((id: string) => handleOrderToggle(id, 'full'))
  }

  const handlePartialSelectionChange = (e: any) => {
    const selectedIds = e.value.map((order: PartialFulfillmentOrder) => order.order_id)
    const currentlySelected = new Set(selectedPartial)
    const newSelection = new Set(selectedIds)
    
    const toAdd = selectedIds.filter((id: string) => !currentlySelected.has(id))
    const toRemove = selectedPartial.filter(id => !newSelection.has(id))
    
    toAdd.forEach((id: string) => handleOrderToggle(id, 'partial'))
    toRemove.forEach((id: string) => handleOrderToggle(id, 'partial'))
  }

  // Column templates
  const orderNameTemplate = (rowData: any) => (
    <div>
      <div className="font-semibold">{rowData.order_name}</div>
      <div className="text-sm text-600">ID: {rowData.order_id}</div>
    </div>
  )

  const itemsTemplate = (rowData: any) => (
    <div className="text-sm">
      {rowData.total_items || rowData.total_quantity_needed || 0} items
      {rowData.total_quantity_needed && (
        <div className="text-xs text-500">{rowData.total_quantity_needed} qty needed</div>
      )}
    </div>
  )

  const statusTemplate = (rowData: any, category: 'full' | 'partial' | 'failed') => {
    const getStatusInfo = () => {
      switch (category) {
        case 'full':
          const fullOrder = rowData as FullFulfillmentOrder
          return {
            text: 'Single store fulfillment',
            detail: fullOrder.recommended_store || fullOrder.store_code || 'Store assigned',
            icon: 'pi-check-circle',
            color: 'text-green-600',
            bgColor: 'bg-green-100'
          }
        case 'partial':
          const partialOrder = rowData as PartialFulfillmentOrder
          return {
            text: 'Multi-store fulfillment',
            detail: `${partialOrder.store_assignments?.length || 0} stores needed`,
            icon: 'pi-exclamation-triangle',
            color: 'text-orange-600',
            bgColor: 'bg-orange-100'
          }
        case 'failed':
          const failedOrder = rowData as FailedOrder
          return {
            text: 'Cannot fulfill',
            detail: failedOrder.reason || 'Insufficient inventory',
            icon: 'pi-times-circle',
            color: 'text-red-600',
            bgColor: 'bg-red-100'
          }
        default:
          return {
            text: 'Unknown',
            detail: 'Unknown status',
            icon: 'pi-question',
            color: 'text-gray-600',
            bgColor: 'bg-gray-100'
          }
      }
    }

    const status = getStatusInfo()
    return (
      <div className={`p-2 border-round ${status.bgColor}`}>
        <div className={`flex align-items-center ${status.color} mb-1`}>
          <i className={`pi ${status.icon} mr-2`} style={{marginRight: '8px'}}></i>
          <span className="font-medium text-sm">{status.text}</span>
        </div>
        <div className="text-xs text-600">{status.detail}</div>
      </div>
    )
  }

  const actionsTemplate = (rowData: FullFulfillmentOrder | PartialFulfillmentOrder | FailedOrder) => (
    <Button
      label="Details"
      icon="pi pi-eye"
      size="small"
      className="p-button-outlined p-button-sm"
      onClick={() => handleViewOrderDetails(rowData)}
    />
  )

  const totalOrders = (processedResults?.full?.length || 0) + (processedResults?.partial?.length || 0) + (processedResults?.failed?.length || 0)
  const fulfillableOrders = (processedResults?.full?.length || 0) + (processedResults?.partial?.length || 0)
  const fulfillmentRate = totalOrders > 0 ? Math.round((fulfillableOrders / totalOrders) * 100) : 0

  return (
    <div>
      {/* Enhanced Summary Section */}
      <div className="mb-5">
        {/* Header with Overall Stats */}
        <div className="mb-4">
          <Card className="shadow-2 border-round-xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="text-white p-2">
              <div className="flex align-items-center justify-content-between">
                <div>
                  <h4 className="m-0 mb-2 text-white">Assignment Analysis Complete</h4>
                  <div className="flex align-items-center gap-3">
                    <div className="flex align-items-center gap-2">
                      <i className="pi pi-check-circle text-xl" style={{marginRight: '4px'}}></i>
                      <span className="text-lg font-semibold">{totalOrders} Orders Processed</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <i className="pi pi-percentage text-xl" style={{marginRight: '4px', marginTop: '8px'}}></i>
                      <span className="text-lg font-semibold">{fulfillmentRate}% Fulfillable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Detailed Metrics Cards */}
        <div 
          className="p-3" 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-around', 
            alignItems: 'center',
            width: '100%',
            gap: '16px'
          }}
        >
          <Card 
            id="full-fulfillment-card"
            style={{ 
              textAlign: 'center', 
              flex: '1',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              padding: '0',
              color: 'white',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
              transition: 'transform 0.2s ease',
              border: 'none',
              cursor: 'help'
            }} 
            className="p-0"
          >
            <div style={{ padding: '12px 16px' }}>
              <div style={{ marginBottom: '4px' }}>
                <i className="pi pi-check-circle" style={{ fontSize: '1.6rem' }}></i>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
                {processedResults?.full?.length || 0}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: '0.9', marginBottom: '8px' }}>
                Full Fulfillment
              </div>
              <Chip 
                label={`${selectedFull.length} Selected`} 
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
                icon="pi pi-check"
              />
            </div>
          </Card>

          <Card 
            id="partial-fulfillment-card"
            style={{ 
              textAlign: 'center', 
              flex: '1',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px',
              padding: '0',
              color: 'white',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
              transition: 'transform 0.2s ease',
              border: 'none',
              cursor: 'help'
            }} 
            className="p-0"
          >
            <div style={{ padding: '12px 16px' }}>
              <div style={{ marginBottom: '4px' }}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.6rem' }}></i>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
                {processedResults?.partial?.length || 0}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: '0.9', marginBottom: '8px' }}>
                Partial Fulfillment
              </div>
              <Chip 
                label={`${selectedPartial.length} Selected`} 
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
                icon="pi pi-minus-circle"
              />
            </div>
          </Card>

          <Card 
            id="failed-orders-card"
            style={{ 
              textAlign: 'center', 
              flex: '1',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '12px',
              padding: '0',
              color: 'white',
              boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
              transition: 'transform 0.2s ease',
              border: 'none',
              cursor: 'help'
            }} 
            className="p-0"
          >
            <div style={{ padding: '12px 16px' }}>
              <div style={{ marginBottom: '4px' }}>
                <i className="pi pi-times-circle" style={{ fontSize: '1.6rem' }}></i>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
                {processedResults?.failed?.length || 0}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: '0.9', marginBottom: '8px' }}>
                Failed Orders
              </div>
              <Chip 
                label="Manual Review" 
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
                icon="pi pi-exclamation-triangle"
              />
            </div>
          </Card>

          <Card 
            id="ready-to-assign-card"
            style={{ 
              textAlign: 'center', 
              flex: '1',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '12px',
              padding: '0',
              color: 'white',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
              transition: 'transform 0.2s ease',
              border: 'none',
              cursor: 'help'
            }} 
            className="p-0"
          >
            <div style={{ padding: '12px 16px' }}>
              <div style={{ marginBottom: '4px' }}>
                <i className="pi pi-shopping-cart" style={{ fontSize: '1.6rem' }}></i>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 'bold', marginBottom: '2px' }}>
                {totalSelected}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: '0.9', marginBottom: '8px' }}>
                Ready to Assign
              </div>
              <Chip 
                label={`${totalOrders > 0 ? Math.round((totalSelected / totalOrders) * 100) : 0}% of total`} 
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontSize: '0.75rem'
                }}
                icon="pi pi-percentage"
              />
            </div>
          </Card>
        </div>

        {/* Tooltips for KPI Cards */}
        <Tooltip target="#full-fulfillment-card" position="top">
          <div className="p-2" style={{ maxWidth: '300px' }}>
            <div className="font-semibold mb-1">Full Fulfillment Orders</div>
            <div className="text-sm">
              Orders that can be completely fulfilled from a single store location. 
            </div>
          </div>
        </Tooltip>

        <Tooltip target="#partial-fulfillment-card" position="top">
          <div className="p-2" style={{ maxWidth: '300px' }}>
            <div className="font-semibold mb-1">Partial Fulfillment Orders</div>
            <div className="text-sm">
              Orders requiring inventory from multiple store locations to be fulfilled. 
            </div>
          </div>
        </Tooltip>

        <Tooltip target="#failed-orders-card" position="top">
          <div className="p-2" style={{ maxWidth: '300px' }}>
            <div className="font-semibold mb-1">Failed Orders</div>
            <div className="text-sm">
              Orders that cannot be fulfilled due to insufficient inventory across all locations. 
              These require manual review and may need alternative sourcing or customer notification.
            </div>
          </div>
        </Tooltip>

        <Tooltip target="#ready-to-assign-card" position="top">
          <div className="p-2" style={{ maxWidth: '300px' }}>
            <div className="font-semibold mb-1">Ready to Assign</div>
            <div className="text-sm">
              Total number of selected orders ready for store assignment. 
              Click 'Confirm Assignment' to proceed with the fulfillment process.
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Results TabView */}
      <div className="mb-4">
        <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
          {/* Full Fulfillment Tab */}
          {processedResults?.full && processedResults.full.length > 0 && (
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-check-circle text-green-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Full Fulfillment</span>
                  <Badge value={processedResults.full.length} className="ml-2" severity="success" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={processedResults.full}
                selection={processedResults.full.filter(order => selectedFull.includes(order.order_id))}
                onSelectionChange={handleFullSelectionChange}
                selectionMode="multiple"
                dataKey="order_id"
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm no-selection-highlight"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={orderNameTemplate}
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="total_items" 
                  header="Items" 
                  body={itemsTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column 
                  header="Status" 
                  body={(rowData) => statusTemplate(rowData, 'full')}
                  style={{ minWidth: '250px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>
          )}

          {/* Partial Fulfillment Tab */}
          {processedResults?.partial && processedResults.partial.length > 0 && (
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-exclamation-triangle text-orange-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Partial Fulfillment</span>
                  <Badge value={processedResults.partial.length} className="ml-2" severity="warning" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={processedResults.partial}
                selection={processedResults.partial.filter(order => selectedPartial.includes(order.order_id))}
                onSelectionChange={handlePartialSelectionChange}
                selectionMode="multiple"
                dataKey="order_id"
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm no-selection-highlight"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={orderNameTemplate}
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="total_quantity_needed" 
                  header="Items" 
                  body={itemsTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column 
                  header="Status" 
                  body={(rowData) => statusTemplate(rowData, 'partial')}
                  style={{ minWidth: '250px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>
          )}

          {/* Failed Orders Tab */}
          {processedResults?.failed && processedResults.failed.length > 0 && (
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-times-circle text-red-600 mr-2" style={{marginRight: '8px'}}></i>
                  <span>Failed Orders</span>
                  <Badge value={processedResults.failed.length} className="ml-2" severity="danger" style={{marginLeft: '8px'}}/>
                </div>
              }
            >
              <DataTable 
                value={processedResults.failed}
                dataKey="order_id"
                size="small"
                stripedRows
                showGridlines
                className="p-datatable-sm no-selection-highlight"
                paginator={true}
                rows={10}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} orders"
              >
                <Column 
                  field="order_name" 
                  header="Order" 
                  body={orderNameTemplate}
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column 
                  field="total_quantity_needed" 
                  header="Items" 
                  body={itemsTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column 
                  header="Status" 
                  body={(rowData) => statusTemplate(rowData, 'failed')}
                  style={{ minWidth: '250px' }}
                />
                <Column 
                  header="Actions" 
                  body={actionsTemplate}
                  style={{ width: '100px' }}
                />
              </DataTable>
            </TabPanel>
          )}
        </TabView>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-content-between">
        <Button
          label="Back to Summary"
          icon="pi pi-arrow-left"
          onClick={handleBackToSummary}
          className="p-button-outlined"
          severity="secondary"
          style={{marginRight: '10px'}}
        />
        <Button
          label={`Confirm Assignment (${totalSelected} orders)`}
          icon="pi pi-check"
          onClick={handleSubmitAssignment}
          className="p-button-primary"
          disabled={totalSelected === 0 || isSubmitting}
          loading={isSubmitting}
        />
      </div>

      {/* Toast */}
      <Toast ref={toast} />

      {/* ViewOrderInfoDialog */}
      <ViewOrderInfoDialog
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={isOrderDialogVisible}
        onHide={() => setIsOrderDialogVisible(false)}
      />
    </div>
  )
} 